import { db } from '../config/firebase';
import { collection, addDoc, serverTimestamp, query, orderBy, getDocs, doc, getDoc, deleteDoc, limit, updateDoc } from "firebase/firestore";

/**
 * Creates a new post document in Firestore.
 * @param {object} postData - Object containing post details.
 * @param {string} postData.userId - The UID of the user creating the post.
 * @param {string} postData.userName - The display name of the user creating the post.
 * @param {string} [postData.characterId] - (Optional) The ID of the character if postType is 'inCharacter'.
 * @param {string} [postData.characterName] - (Optional) The name of the character if postType is 'inCharacter'.
 * @param {string} [postData.characterImageUrl] - (Optional) The image URL of the character if postType is 'inCharacter'.
 * @param {'outOfCharacter' | 'inCharacter' | 'dungeonMaster'} postData.postType - The type of the post.
 * @param {string} postData.title - The title of the post.
 * @param {string} postData.content - The main content of the post.
 * @returns {Promise<string>} The ID of the newly created post document.
 * @throws Will throw an error if postData is invalid or Firestore operation fails.
 */
export const createPost = async (postData) => {
  // Validate required fields
  if (
    !postData ||
    !postData.userId ||
    !postData.userName || // Added validation for userName
    !postData.postType ||
    !postData.title ||
    !postData.content ||
    (postData.postType === 'inCharacter' && !postData.characterId) || // characterId required for inCharacter posts
    (postData.postType === 'inCharacter' && typeof postData.characterName === 'undefined') // characterName required for inCharacter (can be null if not found, but key should exist)
    // No validation for characterImageUrl here, as it's optional even for inCharacter posts
  ) {
    const errorMessage = "Invalid post data provided for creation. Required fields: userId, userName, postType, title, content. If postType is 'inCharacter', characterId and characterName are also required.";
    console.error(errorMessage, { postData });
    throw new Error(errorMessage);
  }

  // Validate postType
  const validPostTypes = ['outOfCharacter', 'inCharacter', 'dungeonMaster'];
  if (!validPostTypes.includes(postData.postType)) {
    const errorMessage = `Invalid postType: ${postData.postType}. Must be one of ${validPostTypes.join(', ')}.`;
    console.error(errorMessage, { postData });
    throw new Error(errorMessage);
  }

  // Ensure characterId is provided if postType is 'inCharacter' (optional based on strictness)
  // For now, we'll allow it to be undefined and rely on application logic to enforce if needed.

  try {
    const newPost = {
      userId: postData.userId,
      userName: postData.userName, // Ensure this is included
      postType: postData.postType,
      title: postData.title,
      content: postData.content,
      characterId: postData.characterId || null,
      characterName: postData.characterName || null, // Ensure this is included
      characterImageUrl: postData.characterImageUrl || null, // Add characterImageUrl
      createdAt: serverTimestamp(), // Firestore server-side timestamp for creation
      updatedAt: serverTimestamp(), // Firestore server-side timestamp for last update
      lastEditedBy: postData.userId, // User creating the post is the first editor
    };

    const docRef = await addDoc(collection(db, "posts"), newPost);
    return docRef.id; // postId is the document ID
  } catch (error) {
    console.error("Error creating post in Firestore:", error);
    throw new Error("Failed to create post. Please try again."); // Generic error for UI
  }
};

/**
 * Fetches all posts from Firestore, ordered by creation date (newest first).
 * @returns {Promise<Array<object>>} A promise that resolves to an array of post objects.
 * @throws Will throw an error if the Firestore operation fails.
 */
export const getAllPosts = async () => {
  try {
    const postsCollectionRef = collection(db, "posts");
    const q = query(postsCollectionRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return posts;
  } catch (error) {
    console.error("Error fetching posts from Firestore:", error);
    throw new Error("Failed to fetch posts. Please try again.");
  }
};

/**
 * Fetches a single post by its ID from Firestore.
 * @param {string} postId - The ID of the post to fetch.
 * @returns {Promise<object|null>} A promise that resolves to the post object if found, or null otherwise.
 * @throws Will throw an error if the Firestore operation fails.
 */
export const getPostById = async (postId) => {
  if (!postId) {
    console.error("Error: postId cannot be empty");
    throw new Error("Post ID cannot be empty.");
  }
  try {
    const postDocRef = doc(db, "posts", postId);
    const docSnap = await getDoc(postDocRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("No such document!");
      return null;
    }
  } catch (error) {
    console.error("Error fetching post by ID from Firestore:", error);
    throw new Error("Failed to fetch post. Please try again.");
  }
};

/**
 * Fetches a limited number of recent posts from Firestore, ordered by creation date (newest first).
 * @param {number} count - The number of recent posts to fetch.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of post objects.
 * @throws Will throw an error if the Firestore operation fails.
 */
export const getRecentPosts = async (count) => {
  try {
    const postsCollectionRef = collection(db, "posts");
    const q = query(postsCollectionRef, orderBy("createdAt", "desc"), limit(count));
    const querySnapshot = await getDocs(q);
    const posts = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return posts;
  } catch (error) {
    console.error("Error fetching recent posts from Firestore:", error);
    throw new Error("Failed to fetch recent posts. Please try again.");
  }
};

/**
 * Deletes a post by its ID from Firestore.
 * @param {string} postId - The ID of the post to delete.
 * @returns {Promise<void>} A promise that resolves when the post is deleted.
 * @throws Will throw an error if the Firestore operation fails.
 */
export const deletePost = async (postId) => {
  if (!postId) {
    console.error("Error: postId cannot be empty for deletion");
    throw new Error("Post ID cannot be empty for deletion.");
  }
  try {
    const postDocRef = doc(db, "posts", postId);
    await deleteDoc(postDocRef);
  } catch (error) {
    console.error("Error deleting post from Firestore:", error);
    throw new Error("Failed to delete post. Please try again.");
  }
};

/**
 * Updates an existing post document in Firestore.
 * @param {string} postId - The ID of the post to update.
 * @param {object} updatedData - Object containing the fields to update.
 * @param {string} updatedData.title - The new title of the post.
 * @param {string} updatedData.content - The new content of the post.
 * @param {string} userId - The UID of the user performing the update.
 * @returns {Promise<void>} A promise that resolves when the post is updated.
 * @throws Will throw an error if postData is invalid or Firestore operation fails.
 */
export const updatePost = async (postId, updatedData, userId) => {
  if (!postId || !updatedData || !userId) {
    throw new Error("Post ID, updated data, and user ID are required for updating a post.");
  }
  if (typeof updatedData.title !== 'string' || typeof updatedData.content !== 'string') {
    throw new Error("Invalid data format for update. Title and content must be strings.");
  }

  try {
    const postDocRef = doc(db, "posts", postId);
    const updatePayload = {
      ...updatedData,
      updatedAt: serverTimestamp(),
      lastEditedBy: userId,
    };
    await updateDoc(postDocRef, updatePayload);
  } catch (error) {
    console.error("Error updating post in Firestore:", error);
    throw new Error("Failed to update post. Please try again.");
  }
};
